import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoIcon from '@mui/icons-material/Info';
import { Box, Grid, Stack, Switch, Tooltip } from '@mui/material';
import { styled } from '@mui/styles';
import { useEffect, useState } from 'react';
import { Controller, FieldValues, FormProvider, useForm } from 'react-hook-form';
import {
  getJobAttr,
  IJobAttrDTO,
  IWorkspace,
  updateJobAttr,
  getCurrentWorkspaceEntity,
} from 'wisestep-api';
import { Label, WsButton, Spinner, WsColors } from 'wisestep-base';

import { useWorkspaceContext } from '../../../context';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../../features/teams/helper/defaultValues';
import { AtsCustomAPIAccess, useAtsCustomAPIAccess } from '../../../hooks/useAtsCustomAPIAccess';
import { success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import JobSettingsLayout from '../components/JobSettingsLayout';
import JobAttrSettings from '../types/JobAttrSettings';

import { defaultAttr, getDefaultJobAttr } from './jobAttrHelper';
import JobAttrSingle from './JobAttrSingle';
const StyledStack = styled(Stack)(() => ({
  top: '0.5vw',
}));

type fieldsArrayType =
  | 'jobTitle'
  | 'jobCompensation'
  | 'jobCode'
  | 'jobLocation'
  | 'jobPosition'
  | 'jobSkills'
  | 'jobDescription'
  | 'jobType'
  | 'jobCompanyName'
  | 'jobCompanyRecruiter'
  | 'jobTags'
  | 'jobClientJobCode'
  | 'jobAccountManager'
  | 'jobInsight'
  | 'jobSubmission'
  | 'jobMapping'
  | 'jobAssignedRecruiters'
  | 'jobActiveRecruiters';
const JobAttrVisibility = () => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [fieldsArray, setFieldsArray] = useState<fieldsArrayType[]>([]);
  const [jobAttrData, setJobAttrData] = useState<IJobAttrDTO>();
  const [jobAttrSettings, setJobAttrSettings] = useState<JobAttrSettings>(getDefaultJobAttr());
  const [workSpaceEntity, setWorkspaceEntity] = useState<IWorkspace>();
  const [hasData, setHasData] = useState<boolean>(false);
  const workspaceContext = useWorkspaceContext();
  const { access: atsCustomAccessControl } = useAtsCustomAPIAccess([
    AtsCustomAPIAccess.MANAGE_JOB_ATTRIBUTE_VISIBILITY_SETTINGS,
  ]);

  const manageAccess = workspaceContext.owns || atsCustomAccessControl[0];

  const formOptions = {
    defaultValues: jobAttrSettings,
  };
  const methodsMain = useForm(formOptions);
  const { handleSubmit, control, reset, setValue } = methodsMain;
  useEffect(() => {
    (async function () {
      const getCurrentWorkspaceEntityReq = await getCurrentWorkspaceEntity();
      const getCurrentWorkspaceEntityRes: IWorkspace = getCurrentWorkspaceEntityReq.data;
      if (getCurrentWorkspaceEntityRes != null) setWorkspaceEntity(getCurrentWorkspaceEntityRes);
      const getJobAttrReq = await getJobAttr();
      const getJobAttrRes: IJobAttrDTO = getJobAttrReq.data;
      if (getJobAttrRes != null) {
        setJobAttrData(getJobAttrRes);
      }
    })();
  }, []);
  const datGen = (tempData) => Object.entries(tempData).map((entry) => entry[0] as fieldsArrayType);

  useEffect(() => {
    if (jobAttrData?.jobAttributesSetting) {
      const tempData = JSON.parse(jobAttrData?.jobAttributesSetting);
      setJobAttrSettings({ ...tempData });
      const configuredData = datGen(tempData);
      reset(tempData);
      setFieldsArray(configuredData);
      reset();
    } else {
      const tempData = defaultAttr;
      setJobAttrSettings({ ...tempData });
      const configuredData = datGen(tempData);
      reset(tempData);
      setFieldsArray(configuredData);
      reset();
    }
    setHasData(true);
  }, [jobAttrData, reset]);
  const onChangeHandlerAdminSettings = (checked) => {
    if (workSpaceEntity) {
      const tempData: IWorkspace = { ...workSpaceEntity };
      if (tempData) {
        tempData.allowJobAdminsToChangeSettings = checked;
        setWorkspaceEntity({ ...tempData });
      }
    }
  };
  const onSave = async (data: FieldValues) => {
    if (workSpaceEntity && jobAttrSettings) {
      const tempData: IWorkspace = { ...workSpaceEntity };
      tempData.jobAttributesSetting = JSON.stringify(data);
      try {
        await updateJobAttr(tempData);
        setNotification(success('Job Attributes Settings Updated Successfully.'));
      } catch (error) {
        setNotification(warning('Something went wrong, Please try again.'));
      }
    }
  };
  const onChange = (key, props, checked: boolean, index: number) => {
    const tempData = props.field.value;
    switch (index) {
      case 0:
        tempData.internal = checked;
        break;
      case 1:
        tempData.external = checked;
        break;
    }
    setValue(key, { ...tempData });
  };

  return (
    <JobSettingsLayout
      title=" Job Attributes Visibility Settings"
      indexLabel="Job Attributes Visibility"
      indexUrl="/job-attribute-visibility-setting"
      breadCrumbsList={getSettingBreadCrumbList(
        ISettingBreadCrumbTypes.JOBS,
        Pages['JOB_ATTRIBUTE_VISIBILITY_SETTING']
      )}
    >
      <Box component="form">
        <Grid container direction="row" justifyContent="space-between">
          {hasData ? (
            <>
              <FormProvider {...methodsMain}>
                <Grid
                  justifyContent="space-evenly"
                  container
                  maxHeight={30}
                  direction="row"
                  spacing={4}
                  mb={4}
                >
                  <Grid item xs={3}>
                    <Label sx={{ fontWeight: 'bold' }}>Job Attributes</Label>
                  </Grid>

                  <Grid item xs={4} display="flex" justifyContent="center">
                    <Label sx={{ fontWeight: 'bold' }}>Assigned Internal Users</Label>
                    <Tooltip title="Teams that are part of your organization under the same team owner.">
                      <InfoIcon sx={{ ml: '0.5vw', mt: '0.5vw' }} />
                    </Tooltip>
                  </Grid>

                  <Grid item xs={4} display="flex" justifyContent="center">
                    <Label sx={{ fontWeight: 'bold' }}>External Users</Label>
                    <Tooltip title="Teams that are not part of your organization but help you close positions.">
                      <InfoIcon sx={{ ml: '0.5vw', mt: '0.5vw' }} />
                    </Tooltip>
                  </Grid>
                  {manageAccess && (
                    <Grid item xs={1} display="flex" justifyContent="flex-end">
                      <WsButton onClick={handleSubmit(onSave)}>Save</WsButton>
                    </Grid>
                  )}
                </Grid>
                <Grid container direction="column" mt={4} rowGap={1} mb={2}>
                  {fieldsArray?.map((key: fieldsArrayType, index) => (
                    <Grid key={index} item container xs={12} maxHeight={25} direction="row">
                      <Grid item xs={4}>
                        {jobAttrSettings[key].disabled ? (
                          <Tooltip title="Attribute is disabled for editing">
                            <StyledStack direction="row">
                              <Label sx={{ fontWeight: 'bold' }}>{key}</Label>
                              <ErrorOutlineIcon
                                sx={{
                                  marginTop: 0.5,
                                  inlineSize: 18,
                                  fill: WsColors.Red300,
                                  cursor: 'pointer',
                                }}
                              />
                            </StyledStack>
                          </Tooltip>
                        ) : (
                          <Label sx={{ fontWeight: 'bold' }}>{key}</Label>
                        )}
                      </Grid>
                      <Controller
                        name={key}
                        control={control}
                        render={(props) => (
                          <>
                            <Grid item xs={4} pl={9}>
                              <StyledStack justifyItems="center" direction="row">
                                <Switch
                                  disabled={!manageAccess || props.field.value.disabled}
                                  checked={props.field.value.internal}
                                  onChange={(_event, checked) => onChange(key, props, checked, 0)}
                                />
                              </StyledStack>
                            </Grid>
                            <Grid item xs={4} pl={9}>
                              <StyledStack direction="row">
                                <Switch
                                  disabled={!manageAccess || props.field.value.disabled}
                                  checked={props.field.value.external}
                                  onChange={(_event, checked) => onChange(key, props, checked, 1)}
                                />
                              </StyledStack>
                            </Grid>
                          </>
                        )}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormProvider>

              <Grid container direction="row" maxHeight={20}>
                <JobAttrSingle
                  adminSwitchValue={workSpaceEntity?.allowJobAdminsToChangeSettings ?? false}
                  dataCallback={(data: boolean) => onChangeHandlerAdminSettings(data)}
                />
              </Grid>
            </>
          ) : (
            <Spinner />
          )}
        </Grid>
      </Box>
    </JobSettingsLayout>
  );
};

export default JobAttrVisibility;
